import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Video, Phone, MapPin } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DIVISIONS = [
  { value: "Relocation Services", label: "Relocation" },
];

const MEETING_TYPES = [
  { value: "Zoom Video Call", label: "Zoom",      Icon: Video },
  { value: "Phone Call",      label: "Phone",     Icon: Phone },
  { value: "In-Person · DC",  label: "In-Person", Icon: MapPin },
];

const TIME_SLOTS = [
  "9:00 AM", "10:00 AM", "11:00 AM",
  "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM",
];

const tomorrowISO = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
};

const initial = () => ({
  name: "",
  email: "",
  phone: "",
  organization: "",
  division: DIVISIONS[0].value,
  meeting_type: MEETING_TYPES[0].value,
  preferred_date: tomorrowISO(),
  preferred_time: TIME_SLOTS[1],
  timezone: "ET",
  notes: "",
});

const ConsultationForm = () => {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/consultation`, form, { timeout: 20000 });
      if (res.data?.success) {
        toast.success("Booking received", { description: res.data.message });
        setForm(initial());
        setDone(true);
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      let msg;
      if (typeof detail === "string") {
        msg = detail;
      } else if (Array.isArray(detail) && detail.length) {
        msg = detail.map((d) => d.msg || JSON.stringify(d)).join("; ");
      } else if (err?.response?.status) {
        msg = `Server error ${err.response.status}. Please try again.`;
      } else if (err?.code === "ECONNABORTED") {
        msg = "Request timed out. Please try again.";
      } else if (err?.message) {
        msg = `Network error: ${err.message}`;
      } else {
        msg = "Could not submit. Please try again.";
      }
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div data-testid="consultation-form-success" className="bg-white border border-gray-200 p-8 md:p-12">
        <div className="eyebrow eyebrow--gold mb-4">Booking Received</div>
        <h3 className="font-serif text-2xl md:text-3xl text-[#000000] mb-4">Your request is in.</h3>
        <p className="text-gray-600 leading-relaxed max-w-prose text-[15px]">
          A member of the Kassim Consulting team will confirm your 15-minute consultation by email
          within one business day, including a calendar invite.
        </p>
        <button type="button" className="btn btn--secondary mt-8" data-testid="consultation-form-reset" onClick={() => setDone(false)}>
          Book Another <span className="btn-arrow">→</span>
        </button>
      </div>
    );
  }

  return (
    <form data-testid="consultation-form" onSubmit={onSubmit} className="bg-white border border-gray-200 p-6 md:p-10 space-y-8" noValidate>

      {/* Contact fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
        <div className="md:col-span-2">
          <label className="kc-label" htmlFor="cf-name">Name <span className="text-[#C5A880]">*</span></label>
          <input id="cf-name" data-testid="consult-input-name" className="kc-input" type="text" autoComplete="name" value={form.name} onChange={(e) => update("name", e.target.value)} required />
        </div>
        <div>
          <label className="kc-label" htmlFor="cf-email">Email <span className="text-[#C5A880]">*</span></label>
          <input id="cf-email" data-testid="consult-input-email" className="kc-input" type="email" autoComplete="email" inputMode="email" value={form.email} onChange={(e) => update("email", e.target.value)} required />
        </div>
        <div>
          <label className="kc-label" htmlFor="cf-phone">Phone</label>
          <input id="cf-phone" data-testid="consult-input-phone" className="kc-input" type="tel" autoComplete="tel" inputMode="tel" value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="Optional" />
        </div>
      </div>

      {/* Topic + meeting format */}
      <div className="grid grid-cols-1 md:grid-cols-1 gap-x-6 gap-y-5">
        <div>
          <label className="kc-label">How would you like to meet?</label>
          <div className="grid grid-cols-3 gap-2">
            {MEETING_TYPES.map(({ value, label, Icon }) => (
              <button
                key={value}
                type="button"
                onClick={() => update("meeting_type", value)}
                data-testid={`consult-meeting-${label.toLowerCase()}`}
                className={`flex items-center justify-center gap-1.5 px-2 py-2.5 text-[12px] tracking-[0.08em] uppercase font-semibold border transition-colors ${
                  form.meeting_type === value
                    ? "border-[#000000] bg-[#000000] text-white"
                    : "border-gray-200 text-gray-700 hover:border-[#000000]"
                }`}
              >
                <Icon size={13} />
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Date + time */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
        <div>
          <label className="kc-label" htmlFor="cf-date">Preferred date <span className="text-[#C5A880]">*</span></label>
          <input id="cf-date" data-testid="consult-input-date" className="kc-input" type="date" min={tomorrowISO()} value={form.preferred_date} onChange={(e) => update("preferred_date", e.target.value)} required />
        </div>
        <div>
          <label className="kc-label" htmlFor="cf-time">Preferred time (ET) <span className="text-[#C5A880]">*</span></label>
          <select id="cf-time" data-testid="consult-input-time" className="kc-input bg-white" value={form.preferred_time} onChange={(e) => update("preferred_time", e.target.value)}>
            {TIME_SLOTS.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
      </div>

      {/* Optional notes */}
      <div>
        <label className="kc-label" htmlFor="cf-notes">Anything else? <span className="text-gray-400 normal-case tracking-normal text-[11px] ml-1">(optional)</span></label>
        <textarea id="cf-notes" data-testid="consult-input-notes" rows={3} className="kc-input resize-none" value={form.notes} onChange={(e) => update("notes", e.target.value)} placeholder="A short note about what you'd like to discuss." />
      </div>

      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 pt-4 border-t border-gray-100">
        <p className="text-[11px] text-gray-500 sm:max-w-xs leading-snug">
          Free, no obligation. We'll confirm by email and send a calendar invite.
        </p>
        <button
          type="submit"
          disabled={submitting}
          data-testid="consult-submit-button"
          className="btn btn--primary disabled:opacity-60 disabled:cursor-not-allowed sm:w-auto w-full justify-center"
        >
          {submitting ? "Booking…" : "Book Consultation"}
          <span className="btn-arrow">→</span>
        </button>
      </div>
    </form>
  );
};

export default ConsultationForm;
